import { Box, Button } from '@mui/material';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import { useStepValidation } from '../hooks';
import { getNextStep, StepKey } from '../stepMeta';
import { ButtonContainer } from './styledComponents';

interface StepNavigationButtonProps {
  step: StepKey;
  children?: React.ReactNode;
  onBeforeNavigate?: () => Promise<boolean> | boolean;
}

export const StepNavigationButton: React.FC<StepNavigationButtonProps> = ({
  step,
  children = getTrans('cvBuilderPage.button.next'),
  onBeforeNavigate
}) => {
  const { isStepComplete, blockedReasonKey, handleStepNavigation } =
    useStepValidation(step);
  const nextStep = getNextStep(step);

  const handleClick = async () => {
    if (onBeforeNavigate) {
      const shouldProceed = await onBeforeNavigate();
      if (!shouldProceed) return;
    }

    await handleStepNavigation();
  };

  return (
    <Box>
      <ButtonContainer>
        <Button
          data-testid="step-navigation-button"
          disabled={!isStepComplete}
          color="secondary"
          variant="contained"
          onClick={handleClick}
          sx={{ minWidth: 252 }}
          fullWidth
        >
          {children}
        </Button>
      </ButtonContainer>

      {/* A disabled button with no explanation is the flow's main dead-click generator:
          the talent can see they can't continue but not why. The reason comes from the
          same predicate that disables the button, so the two can never disagree. */}
      {blockedReasonKey && (
        <Text
          variant="caption"
          data-testid="step-navigation-blocked-reason"
          color="error.main"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          {getTrans(blockedReasonKey)}
        </Text>
      )}

      {/* Tell the talent what's coming, so a 7-step flow stops feeling open-ended. */}
      {isStepComplete && nextStep && nextStep.key !== 'SUMMARY' && (
        <Text
          variant="caption"
          data-testid="step-navigation-next-hint"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          {getTrans('cvBuilderPage.button.nextUp')}{' '}
          {getTrans(nextStep.labelKey)}
        </Text>
      )}
    </Box>
  );
};
